import { useEffect, useState } from "react";

export type Screen =
  | "sign-in"
  | "register"
  | "home"
  | "project"
  | "app"
  | "conversation"
  | "me"
  | "resource"
  | "missing";
export type SearchResourceAddress =
  | { kind: "person"; account: string }
  | { kind: "agent"; account: string }
  | { kind: "repository"; project: string; name: string };

export type Where = {
  screen: Screen;
  path: string;
  invite?: string;
  conversation?: string;
  project?: string;
  app?: string;
  section?: string;
  message?: string;
  file?: string;

  focus?: string;
  resource?: SearchResourceAddress;
};

export function whereIs(path: string): Where {
  const url = new URL(path, "http://rally.local");
  const pathname = url.pathname;
  const invite = /^\/register\/([\w-]+)$/.exec(pathname);
  if (invite?.[1]) return { screen: "register", path, invite: invite[1] };
  const project = /^\/p\/([\w-]+)$/.exec(pathname);
  if (project?.[1]) return { screen: "project", path, project: project[1] };
  const held = /^\/apps\/([\w-]+)$/.exec(pathname);
  if (held?.[1]) return { screen: "app", path, app: held[1] };
  const talk = /^\/c\/([\w-]+)$/.exec(pathname);
  if (talk?.[1]) {
    return {
      screen: "conversation",
      path,
      conversation: talk[1],
      message: url.searchParams.get("message") ?? undefined,
      file: url.searchParams.get("file") ?? undefined,
      app: url.searchParams.get("app") ?? undefined,
      focus: url.searchParams.get("focus") ?? undefined,
    };
  }
  const person = /^\/people\/([\w-]+)$/.exec(pathname);
  if (person?.[1]) return { screen: "resource", path, resource: { kind: "person", account: person[1] } };
  const agent = /^\/agents\/([\w-]+)$/.exec(pathname);
  if (agent?.[1]) return { screen: "resource", path, resource: { kind: "agent", account: agent[1] } };
  const repo = /^\/repos\/([\w-]+)$/.exec(pathname);
  const name = url.searchParams.get("name");
  if (repo?.[1] && name) {
    return { screen: "resource", path, resource: { kind: "repository", project: repo[1], name } };
  }
  const mine = /^\/me(?:\/([\w-]+))?$/.exec(pathname);
  if (mine) return { screen: "me", path, section: mine[1] ?? "profile" };
  if (pathname === "/login") return { screen: "sign-in", path };
  if (pathname === "/") return { screen: "home", path };
  return { screen: "missing", path };
}

export function goTo(path: string): void {
  const here = `${window.location.pathname}${window.location.search}`;
  if (path === here) return;
  window.history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function replaceWith(path: string): void {
  if (path === window.location.pathname) return;
  window.history.replaceState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function useWhere(): Where {
  const [path, setPath] = useState(() => `${window.location.pathname}${window.location.search}`);
  useEffect(() => {
    const again = () => setPath(`${window.location.pathname}${window.location.search}`);
    window.addEventListener("popstate", again);
    return () => window.removeEventListener("popstate", again);
  }, []);
  return whereIs(path);
}
